import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Row, Col, Button, Form, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import Layout from "../../layout/Layout";
import VariantFields from "../../Component/theme-management/VariantFields";
import StatusSwitch from "../../Component/theme-management/StatusSwitch";
import FormSkeleton from "../../Component/theme-management/FormSkeleton";
import ErrorState from "../../Component/theme-management/ErrorState";
import {
  widgetsApi,
  variantsApi,
  themesApi,
  widgetCategoriesApi,
  isAuthError,
  apiErrorMessage,
} from "../../helpers/tmClient";
import {
  WIDGET_SCREEN_OPTIONS,
  WIDGET_SIZE_OPTIONS,
} from "../../helpers/tmConstants";
import { defaultTextStylesFor, buildTextStylesFormData } from "../../helpers/textStyles";

const blankVariant = (category = "") => ({
  widget_type: "",
  preview_image: null,
  widget_image: null,
  clock_image: null,
  quote: "",
  text_color: "",
  font_name: "",
  alignment: "",
  shape: "",
  status: true,
  text_styles: defaultTextStylesFor(category),
  content_padding: null,
  hour_hand_image: null,
  minute_hand_image: null,
  hand_center_x: null,
  hand_center_y: null,
  // config params
  accent_color: null, bolt_color: null,
});

const CONFIG_FIELDS = ["accent_color", "bolt_color"];

const appendConfigFields = (fd, source) => {
  for (const f of CONFIG_FIELDS) {
    const v = source[f];
    fd.append(f, v === null || v === undefined ? "" : String(v));
  }
};

/**
 * ThemeWidgetEdit — update widget metadata and manage its variants
 * live (each variant is created / updated / deleted against the API
 * individually). Duplicate sizes are blocked in the UI and backend.
 */
const ThemeWidgetEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [themes, setThemes] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [values, setValues] = useState(null);
  const [variants, setVariants] = useState([]); // existing (from server), editable in place
  const [newVariant, setNewVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  // Per-variant busy flags — keyed by _id (existing) or "new" (the new
  // variant builder) — disable that variant's own Save/Add/Delete while
  // its request is in flight, without blocking the rest of the page.
  const [variantBusy, setVariantBusy] = useState({});

  // `silent` refreshes (after a variant save/delete/reorder) keep the
  // current form/table visible instead of flashing the full-page
  // skeleton — only the very first mount shows that.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setLoadError(false);
    try {
      const [tRes, dRes, cRes] = await Promise.all([
        themesApi.options(),
        widgetsApi.detail(id),
        widgetCategoriesApi.list(),
      ]);
      if (isAuthError(dRes)) return navigate("/");
      setThemes(tRes.data.data || []);
      const docs = cRes.data.data || [];
      setCategoryOptions(docs.flatMap((d) => (d.get_SubCategory || []).map((s) => ({ value: s.name, label: s.name }))));
      const d = dRes.data.data;
      if (!d?.widget) {
        navigate("/theme-widgets");
        return;
      }
      const w = d.widget;
      setValues({
        theme_id: w.theme_id || "",
        widget_screen: w.widget_screen || "",
        widget_name: w.widget_name || "",
        category_name: w.category_name || "",
        premium: !!w.premium,
        status: !!w.status,
      });
      setVariants((d.variants || []).map((v) => ({ ...v, text_styles: v.text_styles || [], _files: {} })));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setValues((v) => ({ ...v, [name]: type === "checkbox" ? checked : value }));
    // Only the in-progress "new variant" builder reseeds live as the
    // category changes — existing saved variants are left untouched.
    if (name === "category_name") {
      setNewVariant((nv) =>
        nv && (!nv.text_styles || !nv.text_styles.length) ? { ...nv, text_styles: defaultTextStylesFor(value) } : nv
      );
    }
  };
  const onSwitch = (name) => (checked) =>
    setValues((v) => ({ ...v, [name]: checked }));

  const saveMeta = async () => {
    if (!values.widget_screen || !values.widget_name.trim()) {
      toast.error("Screen and name are required", {
        position: "bottom-right",
        autoClose: 1000,
      });
      return;
    }
    setSavingMeta(true);
    try {
      const res = await widgetsApi.update(id, {
        theme_id: values.theme_id || "",
        widget_screen: values.widget_screen,
        widget_name: values.widget_name,
        category_name: values.category_name || "",
        premium: values.premium,
        status: values.status,
      });
      if (res.data.status) {
        toast.success("Widget updated", {
          position: "bottom-right",
          autoClose: 700,
        });
        navigate("/theme-widgets");
      } else {
        toast.error(res.data.response_message || "Update failed", {
          position: "bottom-right",
          autoClose: 1200,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.response_message || "Update failed", {
        position: "bottom-right",
        autoClose: 1200,
      });
    } finally {
      setSavingMeta(false);
    }
  };

  /* ----------------------- existing variants ----------------------- */
  const usedSizes = variants.map((v) => v.widget_type).filter(Boolean);
  const usedSizesWithNew = newVariant
    ? [...usedSizes, newVariant.widget_type].filter(Boolean)
    : usedSizes;

  const editVariantField = (i, field, val) =>
    setVariants((vs) =>
      vs.map((v, idx) => (idx === i ? { ...v, [field]: val } : v)),
    );
  const editVariantFile = (i, field, file) =>
    setVariants((vs) =>
      vs.map((v, idx) =>
        idx === i ? { ...v, _files: { ...v._files, [field]: file } } : v,
      ),
    );

  const saveVariant = async (v) => {
    setVariantBusy((b) => ({ ...b, [v._id]: true }));
    const fd = new FormData();
    fd.append("widget_type", v.widget_type);
    fd.append("quote", v.quote || "");
    fd.append("text_color", v.text_color || "");
    fd.append("font_name", v.font_name || "");
    fd.append("alignment", v.alignment || "");
    fd.append("shape", v.shape || "");
    fd.append("content_padding", v.content_padding || "");
    buildTextStylesFormData(fd, v.text_styles || []);
    fd.append("status", v.status);
    if (v._files?.preview_image)
      fd.append("preview_image", v._files.preview_image);
    if (v._files?.widget_image)
      fd.append("widget_image", v._files.widget_image);
    if (v._files?.clock_image) fd.append("clock_image", v._files.clock_image);
    if (v.hand_center_x != null) fd.append("hand_center_x", v.hand_center_x);
    if (v.hand_center_y != null) fd.append("hand_center_y", v.hand_center_y);
    if (v._files?.hour_hand_image) fd.append("hour_hand_image", v._files.hour_hand_image);
    if (v._files?.minute_hand_image) fd.append("minute_hand_image", v._files.minute_hand_image);
    appendConfigFields(fd, v);
    try {
      const res = await variantsApi.update(v._id, fd);
      if (res.data.status) {
        toast.success("Variant saved", {
          position: "bottom-right",
          autoClose: 700,
        });
        await load({ silent: true });
      } else {
        toast.error(apiErrorMessage(res.data, "Save failed"), {
          position: "bottom-right",
          autoClose: 2200,
        });
      }
    } catch (err) {
      toast.error(apiErrorMessage(err?.response?.data, "Save failed"), {
        position: "bottom-right",
        autoClose: 2200,
      });
    } finally {
      setVariantBusy((b) => ({ ...b, [v._id]: false }));
    }
  };

  const deleteVariant = (v) => {
    const swal = Swal.mixin({
      customClass: {
        confirmButton: "btn btn-success",
        cancelButton: "btn btn-danger me-3",
      },
      buttonsStyling: false,
    });
    swal
      .fire({
        title: `Delete ${v.widget_type} variant?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        reverseButtons: true,
      })
      .then(async (r) => {
        if (r.isConfirmed) {
          setVariantBusy((b) => ({ ...b, [v._id]: true }));
          try {
            await variantsApi.remove(v._id);
            toast.success("Variant deleted", {
              position: "bottom-right",
              autoClose: 700,
            });
            await load({ silent: true });
          } catch {
            toast.error("Delete failed", {
              position: "bottom-right",
              autoClose: 900,
            });
            setVariantBusy((b) => ({ ...b, [v._id]: false }));
          }
        }
      });
  };

  /** Drag & drop reorder of existing variants — persists sort_order. */
  const onVariantDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index)
      return;
    const reordered = Array.from(variants);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setVariants(reordered);
    const items = reordered.map((v, idx) => ({
      id: v._id,
      sort_order: idx + 1,
    }));
    try {
      await variantsApi.updateSort(items);
      toast.success("Variant order updated", {
        position: "bottom-right",
        autoClose: 700,
      });
      load({ silent: true });
    } catch {
      toast.error("Failed to save order", {
        position: "bottom-right",
        autoClose: 900,
      });
      load({ silent: true });
    }
  };

  /* ------------------------- new variant --------------------------- */
  const saveNewVariant = async () => {
    if (!newVariant.preview_image || !newVariant.widget_image)
      return toast.error("Preview and widget images are required", {
        position: "bottom-right",
        autoClose: 1200,
      });
    const fd = new FormData();
    fd.append("widget_id", id);
    fd.append("widget_type", newVariant.widget_type);
    fd.append("quote", newVariant.quote || "");
    fd.append("text_color", newVariant.text_color || "");
    fd.append("font_name", newVariant.font_name || "");
    fd.append("alignment", newVariant.alignment || "");
    fd.append("shape", newVariant.shape || "");
    fd.append("content_padding", newVariant.content_padding || "");
    buildTextStylesFormData(fd, newVariant.text_styles || []);
    fd.append("status", newVariant.status);
    fd.append("preview_image", newVariant.preview_image);
    fd.append("widget_image", newVariant.widget_image);
    if (newVariant.clock_image)
      fd.append("clock_image", newVariant.clock_image);
    if (newVariant.hand_center_x != null) fd.append("hand_center_x", newVariant.hand_center_x);
    if (newVariant.hand_center_y != null) fd.append("hand_center_y", newVariant.hand_center_y);
    if (newVariant.hour_hand_image) fd.append("hour_hand_image", newVariant.hour_hand_image);
    if (newVariant.minute_hand_image) fd.append("minute_hand_image", newVariant.minute_hand_image);
    appendConfigFields(fd, newVariant);
    setVariantBusy((b) => ({ ...b, new: true }));
    try {
      const res = await variantsApi.create(fd);
      if (res.data.status) {
        toast.success("Variant added", {
          position: "bottom-right",
          autoClose: 700,
        });
        setNewVariant(null);
        await load({ silent: true });
      } else {
        toast.error(apiErrorMessage(res.data, "Add failed"), {
          position: "bottom-right",
          autoClose: 2200,
        });
      }
    } catch (err) {
      toast.error(apiErrorMessage(err?.response?.data, "Add failed"), {
        position: "bottom-right",
        autoClose: 2200,
      });
    } finally {
      setVariantBusy((b) => ({ ...b, new: false }));
    }
  };

  if (loading) {
    return (
      <Layout sidebar={true}>
        <div className="page-heading"><h3 className="my-1">Edit Widget</h3></div>
        <div className="page-content">
          <FormSkeleton fields={4} rows={2} />
        </div>
      </Layout>
    );
  }
  if (loadError || !values) {
    return (
      <Layout sidebar={true}>
        <div className="page-heading"><h3 className="my-1">Edit Widget</h3></div>
        <div className="page-content">
          <Card><Card.Body>
            <ErrorState asRow={false} message="Failed to load widget." onRetry={load} />
          </Card.Body></Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout sidebar={true}>
      <div className="page-heading">
        <h3 className="my-1">Edit Widget</h3>
      </div>
      <div className="page-content">
        <Card className="mb-4">
          <Card.Body>
            <fieldset disabled={savingMeta} style={{ border: "none", padding: 0, margin: 0 }}>
            <Row>
              <Col lg={4} sm={12}>
                <Form.Group>
                  <Form.Label>Theme</Form.Label>
                  <Form.Select
                    name="theme_id"
                    className="my-2"
                    value={values.theme_id}
                    onChange={onChange}
                  >
                    <option value="">None (standalone)</option>
                    {themes.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.name} ({t.theme_type})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col lg={4} sm={12}>
                <Form.Group>
                  <Form.Label>
                    Widget Screen <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="widget_screen"
                    className="my-2"
                    value={values.widget_screen}
                    onChange={onChange}
                  >
                    <option value="">Select Screen</option>
                    {WIDGET_SCREEN_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col lg={4} sm={12}>
                <Form.Group>
                  <Form.Label>
                    Widget Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="widget_name"
                    className="my-2"
                    value={values.widget_name}
                    onChange={onChange}
                  />
                </Form.Group>
              </Col>
              <Col lg={4} sm={12}>
                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    name="category_name"
                    className="my-2"
                    value={values.category_name}
                    onChange={onChange}
                  >
                    <option value="">Select Category</option>
                    {categoryOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col lg={3} sm={6}>
                <Form.Label className="d-block">Premium</Form.Label>
                <StatusSwitch
                  checked={values.premium}
                  disabled={savingMeta}
                  onChange={onSwitch("premium")}
                  onLabel="ON"
                  offLabel="OFF"
                />
              </Col>
              <Col lg={3} sm={6}>
                <Form.Label className="d-block">Active</Form.Label>
                <StatusSwitch
                  checked={values.status}
                  disabled={savingMeta}
                  onChange={onSwitch("status")}
                />
              </Col>
            </Row>
            </fieldset>
          </Card.Body>
          <Card.Footer className="text-end">
            <Button
              variant="primary"
              className="me-3 d-inline-flex align-items-center gap-2"
              onClick={saveMeta}
              disabled={savingMeta}
            >
              {savingMeta && <span className="spinner-border spinner-border-sm" role="status"></span>}
              {savingMeta ? "Saving..." : "Save Widget"}
            </Button>
            <Link to="/theme-widgets">
              <Button variant="secondary" disabled={savingMeta}>Back</Button>
            </Link>
          </Card.Footer>
        </Card>

        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>Variants ({variants.length})</strong>
            <Button
              variant="primary"
              size="sm"
              disabled={
                !!newVariant || variants.length >= WIDGET_SIZE_OPTIONS.length
              }
              onClick={() => setNewVariant(blankVariant(values.category_name))}
            >
              <i className="bx bx-plus"></i> Add Variant
            </Button>
          </Card.Header>
          <Card.Body>
            {variants.length === 0 && !newVariant && (
              <p className="text-muted mb-0">No variants yet.</p>
            )}

            <DragDropContext onDragEnd={onVariantDragEnd}>
              <Droppable droppableId="variants-droppable">
                {(provider) => (
                  <div ref={provider.innerRef} {...provider.droppableProps}>
                    {variants.map((v, i) => (
                      <Draggable key={v._id} draggableId={v._id} index={i}>
                        {(prov) => (
                          <div ref={prov.innerRef} {...prov.draggableProps}>
                            <div className="d-flex align-items-center mt-2">
                              <i
                                className="bx bx-menu me-2 text-muted"
                                title="Drag to reorder"
                                style={{ cursor: "grab", fontSize: 20 }}
                                {...prov.dragHandleProps}
                              ></i>
                              <small className="text-muted">
                                Drag to reorder
                              </small>
                            </div>
                            <VariantFields
                              title={`Variant ${i + 1}`}
                              value={v}
                              category={values.category_name}
                              existing={{
                                preview_image: v.preview_image,
                                widget_image: v.widget_image,
                                clock_image: v.clock_image,
                                hour_hand_image: v.hour_hand_image,
                                minute_hand_image: v.minute_hand_image,
                              }}
                              usedSizes={usedSizes}
                              disabled={!!variantBusy[v._id]}
                              onField={(f, val) => editVariantField(i, f, val)}
                              onFile={(f, file) => editVariantFile(i, f, file)}
                              onRemove={() => deleteVariant(v)}
                            />
                            <div className="text-end mb-2">
                              <Button
                                variant="success"
                                size="sm"
                                className="d-inline-flex align-items-center gap-2"
                                disabled={!!variantBusy[v._id]}
                                onClick={() => saveVariant(v)}
                              >
                                {variantBusy[v._id] && <span className="spinner-border spinner-border-sm" role="status"></span>}
                                {variantBusy[v._id] ? "Saving..." : "Save Variant"}
                              </Button>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provider.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {newVariant && (
              <div>
                <VariantFields
                  title="New Variant"
                  value={newVariant}
                  category={values.category_name}
                  usedSizes={usedSizesWithNew}
                  disabled={!!variantBusy.new}
                  onField={(f, val) =>
                    setNewVariant((nv) => ({ ...nv, [f]: val }))
                  }
                  onFile={(f, file) =>
                    setNewVariant((nv) => ({ ...nv, [f]: file }))
                  }
                  onRemove={() => setNewVariant(null)}
                />
                <div className="text-end mb-2">
                  <Button
                    variant="success"
                    size="sm"
                    className="me-2 d-inline-flex align-items-center gap-2"
                    disabled={!!variantBusy.new}
                    onClick={saveNewVariant}
                  >
                    {variantBusy.new && <span className="spinner-border spinner-border-sm" role="status"></span>}
                    {variantBusy.new ? "Adding..." : "Add Variant"}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!!variantBusy.new}
                    onClick={() => setNewVariant(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </Layout>
  );
};

export default ThemeWidgetEdit;
